import { writable } from 'svelte/store'
import { config } from '../config.js'
import { format, addMonths, differenceInCalendarDays } from 'date-fns'
import { get } from '../services/http.js'

export function getReturnDate(orderDate) {
  let d

  if (!orderDate) d = new Date()
  else d = new Date(orderDate)

  d = addMonths(d, 2)
  let diff = differenceInCalendarDays(d, new Date())
  return `Return puzzles by ${format(d, 'MM/dd/yyyy')} (${diff} days)`
}

function createPuzzlePack() {
  const isInit = () => {
    let init = false
    subscribe((pp) => {
      init = pp.initialized
    })
    return init
  }

  const getItemCount = () => {
    let itemCount
    subscribe((pp) => {
      itemCount = pp.itemCount
    })
    return itemCount
  }

  const getMax = (attributes) => {
    for (let i in attributes) {
      if (attributes[i].name === 'Max # of Puzzles') {
        return parseInt(attributes[i].value)
      }
    }
  }

  const initialize = (sku, itemCount) => {
    update((pp) => {
      pp.initialized = true
      pp.maxPuzzles = pp.options[sku].maxPuzzles
      pp.selectedSku = sku
      pp.itemCount = itemCount || 0
      return pp
    })
  }

  const { subscribe, set, update } = writable({
    errorMsg: null,
    options: {},
    selectedSku: null,
    itemCount: 0,
    maxPuzzles: 0,
    initialized: false,
    details: {},
    maxRentalOption: -1,
    minRentalOption: 100,
  })

  return {
    subscribe,

    loadAll: async () => {
      let url = `${config.apiUrl}/products?product-type-id=4&sort-by=price-asc`
      let result = (await get(url, false)) || []
      update((pp) => {
        for (let i in result.products) {
          let opt = result.products[i]
          pp.options[opt.sku] = opt
          pp.options[opt.sku].maxPuzzles = getMax(opt.attributes)
          if (pp.options[opt.sku].maxPuzzles > pp.maxRentalOption) {
            pp.maxRentalOption = pp.options[opt.sku].maxPuzzles
          }
          if (pp.options[opt.sku].maxPuzzles < pp.minRentalOption) {
            pp.minRentalOption = pp.options[opt.sku].maxPuzzles
          }
        }
        return pp
      })
    },

    getItemCount: () => {
      return getItemCount()
    },

    setError: (err) => {
      update((puzzlePack) => {
        puzzlePack.errorMsg = err
        return puzzlePack
      })
    },

    clearError: () => {
      update((puzzlePack) => {
        puzzlePack.errorMsg = null
        return puzzlePack
      })
    },

    getSKU: () => {
      let sku = null
      subscribe((pp) => {
        sku = pp.selectedSku
      })
      return sku
    },

    checkCart: (p, action) => {
      if (
        action === 'add' &&
        p.product_type.name === 'Puzzle Pack' &&
        isInit()
      ) {
        let errorMsg = 'You can only have 1 puzzle pack per order'
        update((puzzlePack) => {
          puzzlePack.errorMsg = errorMsg
          return puzzlePack
        })
        return false
      } else if (
        action === 'remove' &&
        p.product_type.name === 'Puzzle Pack' &&
        getItemCount() > 0
      ) {
        let errorMsg =
          'You cannot remove a puzzle pack if there are rentals in your cart'
        update((puzzlePack) => {
          puzzlePack.errorMsg = errorMsg
          return puzzlePack
        })
        return false
      } else if (
        action === 'add' &&
        p.product_type.name === 'Subscription' &&
        isInit()
      ) {
        let errorMsg =
          'You cannot sign up for a subscription and order a puzzle pack'
        update((puzzlePack) => {
          puzzlePack.errorMsg = errorMsg
          return puzzlePack
        })
        return false
      } else if (
        action === 'swap' &&
        p.product_type.name === 'Puzzle Pack' &&
        p.maxPuzzles < getItemCount()
      ) {
        let errorMsg = `You have more rentals in your cart than the max allowed for ${p.title}. Please remove some rentals in your cart to proceed to switch Puzzle Packs.`
        update((puzzlePack) => {
          puzzlePack.errorMsg = errorMsg
          return puzzlePack
        })
        return false
      }

      return true
    },

    addItem: () => {
      update((pp) => {
        pp.itemCount++
        return pp
      })
    },
    removeItem: () => {
      update((pp) => {
        pp.itemCount--
        return pp
      })
    },
    isFull: () => {
      let isFull = false
      let errorMsg

      subscribe((pp) => {
        if (pp.itemCount === pp.maxPuzzles) {
          isFull = true
          errorMsg = `Your Puzzle Pack quota of ${pp.maxPuzzles} puzzles is complete, please remove an item if you wish to add this rental.`
        }
      })

      if (errorMsg) {
        update((puzzlePack) => {
          puzzlePack.errorMsg = errorMsg
          return puzzlePack
        })
      }

      return isFull
    },
    setInit: (sku, itemCount) => {
      initialize(sku, itemCount)
    },
    isInit: () => {
      return isInit()
    },
    setCount: (items) => {
      let itemCount = 0
      let hasPuzzlePack = { sku: null }
      for (let i in items) {
        if (items[i].product_type.name === 'Puzzle (Rental)')
          itemCount += items[i].quantity
        else if (items[i].product_type.name === 'Puzzle Pack') {
          hasPuzzlePack.sku = items[i].sku
        }
      }

      if (hasPuzzlePack.sku) {
        initialize(hasPuzzlePack.sku, itemCount)
      }
    },

    // Checks that the number of rentals in cart is a valid amount.
    checkItemCountValid: () => {
      let valid = true
      let msg = ''
      update((pp) => {
        // Must have more than zero. We could also let people just give away
        // their money. That's cool.
        if (pp.itemCount <= 0) {
          valid = false
          msg =
            'You have no puzzle pack rentals in your cart! ' +
            'Please select at least one puzzle rental to checkout.'

          // If they somehow added too many puzzle packs. That's not cool.
        } else if (pp.itemCount > pp.maxPuzzles) {
          valid = false
          msg =
            'You have too many puzzle pack rentals in your cart! ' +
            'Remove puzzle rentals to checkout!'
        }

        return pp
      })

      return {
        valid: valid,
        message: msg,
      }
    },

    reset: () => {
      update((pp) => {
        pp.itemCount = 0
        pp.initialized = false
        pp.maxPuzzles = 0
        return pp
      })
    },
  }
}

export let puzzlePack = createPuzzlePack()
